import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Table from "react-bootstrap/esm/Table";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import { loadGemeindeSettings, replaceGemeindeSettingsFields } from "../services/gemeindeSettingsService";
import {
    IMPORT_CODE_FIELDS,
    ImportCodeKind,
    ImportCodeRow,
    importCodeMapOf,
    importCodeRowsOf,
    MAX_IMPORT_CODE_LENGTH,
    MAX_IMPORT_NAME_LENGTH,
} from "../utils/importCodes";
import { handleFirebaseError } from "../utils/errorHandler";
import I_IMC_Props from "../infos/I_IMC_Props";

const KINDS: ImportCodeKind[] = ["houses", "serviceCodes"];

type Rows = Record<ImportCodeKind, ImportCodeRow[]>;

/**
 * The codes the sermon-plan import understands (GOD-48): Gemeindehaus codes in
 * preacher cells and occasion codes in the row under "Datum". Until a Gemeinde
 * saves its own, the built-in values apply.
 */
export default function ImportCodesEditor({
    gemeindeId,
    setVariant,
    setMessage,
    setShowMessage,
}: I_IMC_Props & { gemeindeId: string }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState<Rows | null>(null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        let cancelled = false;
        setRows(null);
        loadGemeindeSettings(gemeindeId)
            .then((settings) => {
                if (cancelled) return;
                setRows({
                    houses: importCodeRowsOf(settings, "houses"),
                    serviceCodes: importCodeRowsOf(settings, "serviceCodes"),
                });
            })
            .catch((err) => {
                if (cancelled) return;
                setVariant("danger");
                setMessage(handleFirebaseError(err, "loadImportCodes"));
                setShowMessage(true);
            });
        return () => {
            cancelled = true;
        };
    }, [gemeindeId, setVariant, setMessage, setShowMessage]);

    if (!rows) return <Spinner size="sm" animation="border" />;

    const update = (kind: ImportCodeKind, index: number, field: keyof ImportCodeRow, value: string) =>
        setRows({ ...rows, [kind]: rows[kind].map((row, i) => (i === index ? { ...row, [field]: value } : row)) });
    const remove = (kind: ImportCodeKind, index: number) =>
        setRows({ ...rows, [kind]: rows[kind].filter((_, i) => i !== index) });
    const add = (kind: ImportCodeKind) => setRows({ ...rows, [kind]: [...rows[kind], { code: "", name: "" }] });
    const resetDefaults = () =>
        setRows({
            houses: importCodeRowsOf({}, "houses"),
            serviceCodes: importCodeRowsOf({}, "serviceCodes"),
        });

    async function save() {
        if (!rows) return;
        const fields: Record<string, Record<string, string>> = {};
        const problems: string[] = [];
        for (const kind of KINDS) {
            const { map, errors: kindErrors } = importCodeMapOf(rows[kind]);
            fields[IMPORT_CODE_FIELDS[kind]] = map;
            for (const { error, code } of kindErrors) {
                problems.push(`${t(`admin.importCodes.${kind}.title`)}: ${t(`admin.importCodes.errors.${error}`, { code })}`);
            }
        }
        setErrors(problems);
        if (problems.length > 0) return;
        setSaving(true);
        try {
            await replaceGemeindeSettingsFields(gemeindeId, fields);
            setVariant("success");
            setMessage(t("admin.importCodes.saved"));
            setShowMessage(true);
        } catch (err) {
            setVariant("danger");
            setMessage(handleFirebaseError(err, "saveImportCodes"));
            setShowMessage(true);
        } finally {
            setSaving(false);
        }
    }

    return (
        <div data-testid="import-codes">
            {KINDS.map((kind) => (
                <div key={kind} className="mb-4">
                    <h5>{t(`admin.importCodes.${kind}.title`)}</h5>
                    <p className="text-muted small mb-2">{t(`admin.importCodes.${kind}.help`)}</p>
                    <Table size="sm" className="mb-2" style={{ maxWidth: 560 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 140 }}>{t("admin.importCodes.code")}</th>
                                <th>{t(`admin.importCodes.${kind}.name`)}</th>
                                <th style={{ width: 48 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows[kind].map((row, index) => (
                                <tr key={index}>
                                    <td>
                                        <Form.Control
                                            size="sm"
                                            value={row.code}
                                            maxLength={MAX_IMPORT_CODE_LENGTH}
                                            aria-label={t("admin.importCodes.code") as string}
                                            onChange={(e) => update(kind, index, "code", e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            size="sm"
                                            value={row.name}
                                            maxLength={MAX_IMPORT_NAME_LENGTH}
                                            aria-label={t(`admin.importCodes.${kind}.name`) as string}
                                            onChange={(e) => update(kind, index, "name", e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            aria-label={t("admin.importCodes.remove") as string}
                                            onClick={() => remove(kind, index)}
                                        >
                                            ×
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Button size="sm" variant="outline-secondary" onClick={() => add(kind)} data-testid={`add-${kind}`}>
                        {t("admin.importCodes.add")}
                    </Button>
                </div>
            ))}
            {errors.length > 0 && (
                <Alert variant="warning">
                    {errors.map((error) => (
                        <div key={error}>{error}</div>
                    ))}
                </Alert>
            )}
            <div className="d-flex gap-2">
                <Button onClick={save} disabled={saving} data-testid="save-import-codes">
                    {saving ? <Spinner size="sm" animation="border" /> : t("admin.importCodes.save")}
                </Button>
                <Button variant="outline-secondary" onClick={resetDefaults} disabled={saving}>
                    {t("admin.importCodes.resetDefaults")}
                </Button>
            </div>
        </div>
    );
}
